import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalConfirmacionPagoSuscripcion from './ModalConfirmacionPagoSuscripcion';

const ModalNuevoModuloLicenciaCliente = ({
    show,
    onHide,
    idLicencia,
    onCreated
}) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const [loadingContext, setLoadingContext] = useState(false);
    const [saving, setSaving] = useState(false);
    const [contextError, setContextError] = useState('');
    const [context, setContext] = useState(null);   // { idSuscripcion, vTipoRenovacion, vMoneda, ... }
    const [productos, setProductos] = useState([]); // filas: un registro por renovacion disponible

    const [selectedProducto, setSelectedProducto] = useState('');
    const [selectedRenovacion, setSelectedRenovacion] = useState('');

    const [showConfirmacionPago, setShowConfirmacionPago] = useState(false);
    const [asignacionResult, setAsignacionResult] = useState(null); // { idSuscripcion, idSuscripcionDetalle, dTotal, vMoneda }

    // Opciones únicas de producto (sin duplicados)
    const productosOpciones = productos.reduce((acc, p) => {
        if (!acc.find(x => x.idProducto === p.idProducto)) {
            acc.push({
                idProducto: p.idProducto,
                vNombreProducto: p.vNombreProducto,
                vDescripcionModulo: p.vDescripcionModulo
            });
        }
        return acc;
    }, []);

    // Renovaciones para el producto seleccionado
    const renovacionesParaProducto = productos.filter(
        p => String(p.idProducto) === String(selectedProducto)
    );

    // Renovación actualmente seleccionada (objeto completo)
    const renovacionSeleccionada = renovacionesParaProducto.find(
        r => String(r.idProductoRenovaciones) === String(selectedRenovacion)
    );

    const cargarContexto = async () => {
        if (!idLicencia) return;
        setLoadingContext(true);
        setContextError('');
        try {
            const resp = await fetch(
                `/api/licencia/ObtenerContextoAltaModulo?idLicencia=${idLicencia}`,
                { headers: { Authorization: 'Bearer ' + token } }
            );
            const data = await resp.json();
            if (data.iResponseType === 1 && data.detalles) {
                const det = JSON.parse(data.detalles);
                setContext(det.table2?.[0] || null);
                setProductos(Array.isArray(det.table3) ? det.table3 : []);
            } else {
                setContextError(data.vMessage || 'Error al obtener contexto de suscripción');
            }
        } catch (err) {
            setContextError('Error al cargar contexto: ' + err.message);
        } finally {
            setLoadingContext(false);
        }
    };

    // Cargar/limpiar al abrir o cerrar
    useEffect(() => {
        if (show) {
            cargarContexto();
        } else {
            setContext(null);
            setProductos([]);
            setSelectedProducto('');
            setSelectedRenovacion('');
            setContextError('');
            setAsignacionResult(null);
            setShowConfirmacionPago(false);
        }
    }, [show, idLicencia]);

    // Auto-seleccionar renovación que coincide con el periodo de la suscripción
    useEffect(() => {
        if (!selectedProducto || !context) return;
        const renvs = productos.filter(p => String(p.idProducto) === String(selectedProducto));
        const matching = renvs.find(r => r.vTipoRenovacion === context.vTipoRenovacion);
        if (matching) {
            setSelectedRenovacion(String(matching.idProductoRenovaciones));
        } else if (renvs.length === 1) {
            setSelectedRenovacion(String(renvs[0].idProductoRenovaciones));
        } else {
            setSelectedRenovacion('');
        }
    }, [selectedProducto, context]);

    const handleGuardar = async () => {
        if (!selectedProducto) return showErrorToast('Selecciona un módulo');
        if (!selectedRenovacion) return showErrorToast('Selecciona un tipo de renovación');
        if (!context?.idSuscripcion) return showErrorToast('No se encontró suscripción activa');

        setSaving(true);
        try {
            const payload = {
                idLicencia: parseInt(idLicencia),
                idSuscripcion: context.idSuscripcion,
                idProducto: parseInt(selectedProducto),
                idProductoRenovaciones: parseInt(selectedRenovacion)
            };

            const resp = await fetch('/api/Suscripciones/AsignarModuloConSuscripcion', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const data = await resp.json();
            if (data.iResponseType === 1) {
                const det = data.detalles ? JSON.parse(data.detalles) : {};
                setAsignacionResult({
                    idSuscripcion:        det.idSuscripcion        || context.idSuscripcion,
                    idSuscripcionDetalle: det.idSuscripcionDetalle || 0,
                    dTotal:               renovacionSeleccionada?.dTotal || 0,
                    vMoneda:              renovacionSeleccionada?.vMoneda || context.vMoneda || 'MXN'
                });
                setShowConfirmacionPago(true);
            } else {
                showErrorToast(data.vMessage || 'Error al asignar módulo');
            }
        } catch (err) {
            showErrorToast('Error al asignar módulo: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmacionPago = async (cobrarAhora) => {
        setShowConfirmacionPago(false);

        if (cobrarAhora && asignacionResult?.idSuscripcionDetalle > 0) {
            try {
                const resp = await fetch('/api/Cliente/RegistrarCobroModulo', {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        idSuscripcion:        asignacionResult.idSuscripcion,
                        idSuscripcionDetalle: asignacionResult.idSuscripcionDetalle,
                        dDiferencia:          asignacionResult.dTotal
                    })
                });
                const data = await resp.json();
                if (data.iResponseType === 1) {
                    showSuccessToast('Módulo asignado y cobro registrado correctamente');
                } else {
                    showErrorToast('Módulo asignado, pero el cobro falló: ' + (data.vMessage || 'Error desconocido'));
                }
            } catch (err) {
                showErrorToast('Módulo asignado, pero error al registrar cobro: ' + err.message);
            }
        } else {
            showSuccessToast('Módulo asignado. El cobro se realizará en la siguiente renovación.');
        }

        onCreated();
    };

    return (
        <>
        <Modal show={show} onHide={onHide} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Agregar Módulo a Suscripción</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loadingContext ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : contextError ? (
                    <Alert variant="danger">{contextError}</Alert>
                ) : (
                    <Row className="g-3">
                        {context && (
                            <Col xs={12}>
                                <Alert variant="info" className="py-2 mb-0 small">
                                    <strong>Suscripción #{context.idSuscripcion}</strong>
                                    {context.vTipoRenovacion && ` · ${context.vTipoRenovacion}`}
                                </Alert>
                            </Col>
                        )}

                        <Col xs={12}>
                            <Form.Label>
                                Módulo <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={selectedProducto}
                                onChange={e => {
                                    setSelectedProducto(e.target.value);
                                    setSelectedRenovacion('');
                                }}
                            >
                                <option value=''>— Seleccionar módulo —</option>
                                {productosOpciones.map(p => (
                                    <option key={p.idProducto} value={p.idProducto}>
                                        {p.vNombreProducto}
                                        {p.vDescripcionModulo ? ` (${p.vDescripcionModulo})` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>

                        {selectedProducto && (
                            <Col xs={12}>
                                <Form.Label>
                                    Tipo de Renovación <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={selectedRenovacion}
                                    onChange={e => setSelectedRenovacion(e.target.value)}
                                >
                                    <option value=''>— Seleccionar renovación —</option>
                                    {renovacionesParaProducto.map(r => (
                                        <option key={r.idProductoRenovaciones} value={r.idProductoRenovaciones}>
                                            {r.vTipoRenovacion} — ${r.dTotal?.toFixed(2)} {r.vMoneda || ''}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        )}

                        {renovacionSeleccionada && (
                            <Col xs={12}>
                                <Alert variant="success" className="py-2 mb-0 small">
                                    <strong>Subtotal:</strong> ${renovacionSeleccionada.dPrecioUnitario?.toFixed(2)}
                                    {' · '}
                                    <strong>IVA:</strong> ${renovacionSeleccionada.dIVA?.toFixed(2)}
                                    {' · '}
                                    <strong>Total:</strong> ${renovacionSeleccionada.dTotal?.toFixed(2)} {renovacionSeleccionada.vMoneda || ''}
                                </Alert>
                            </Col>
                        )}
                    </Row>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleGuardar}
                    disabled={saving || loadingContext || !!contextError || !selectedRenovacion}
                >
                    {saving
                        ? <><Spinner size="sm" className="me-1" />Guardando...</>
                        : 'Asignar Módulo'
                    }
                </Button>
            </Modal.Footer>
        </Modal>

        <ModalConfirmacionPagoSuscripcion
            show={showConfirmacionPago}
            onHide={() => {
                setShowConfirmacionPago(false);
                onCreated();
            }}
            diferencia={asignacionResult?.dTotal || 0}
            moneda={asignacionResult?.vMoneda || 'MXN'}
            onConfirm={handleConfirmacionPago}
            textoDescripcion="Al agregar este módulo, la suscripción aumentará su costo."
            textoNota="El módulo será asignado inmediatamente, independientemente de tu selección."
        />
        </>
    );
};

ModalNuevoModuloLicenciaCliente.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    infoSuscripcion: PropTypes.object,
    onCreated: PropTypes.func.isRequired
};

export default ModalNuevoModuloLicenciaCliente;
